import Tooltip, { useAnalysisActiveDot } from './AnalysisTooltip';
import { Checkbox } from './pilot-ui/checkbox';
import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { ChartData } from '../types';
import ChartCsvButton from './ChartCsvButton';
import { CsvColumn } from '../utils/chartCsv';
import { bandDomain, measureRange } from './PressureChart';
import { AXIS_TITLE_STYLE, X_TICK_MARGIN, inH2OTickFormatter, referenceLabel, sharedPaDomain } from '../utils/pressureAxis';
import { DATA_LINE_Z_INDEX, REFERENCE_LINE_Z_INDEX } from './referenceLineLayer';

interface CumulativeRuntimeChartProps {
  jsonData: ChartData;
}

// Browser-remembered preference: anchor the pressure axis at zero instead of
// letting recharts fit the data range.
const ZERO_PREF_KEY = 'cumulativeRuntimeChart.includeZero';

const CumulativeRuntimeChart: React.FC<CumulativeRuntimeChartProps> = ({ jsonData }) => {
  const activeDot = useAnalysisActiveDot(true);
  const [includeZero, setIncludeZero] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(ZERO_PREF_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggleIncludeZero = () => {
    const next = !includeZero;
    setIncludeZero(next);
    try {
      localStorage.setItem(ZERO_PREF_KEY, next ? '1' : '0');
    } catch {
      // storage unavailable (private mode) — preference just won't persist
    }
  };
  if (!jsonData) return null;

  // Find the last date and calculate the cutoff date (30 days before)
  const lastDate = new Date(jsonData.dates[jsonData.dates.length - 1]);
  const cutoffDate = new Date(lastDate);
  cutoffDate.setDate(cutoffDate.getDate() - 30);

  // Find the last non-null pressure value and its index for trend line calculation
  let lastPressureIndex = -1;
  let lastPressureValue = 0;
  let lastCumulativeRuntime = 0;
  for (let i = jsonData.pressures.length - 1; i >= 0; i--) {
    if (jsonData.pressures[i] != null) {
      lastPressureIndex = i;
      lastPressureValue = jsonData.pressures[i];
      lastCumulativeRuntime = jsonData.cumulative_runtimes[i];
      break;
    }
  }

  // Create chart data by combining runtimes with pressures (now already in hours)
  // All arrays are aligned by index from the backend
  const chartData = jsonData.dates.map((date, i) => {
    const currentDate = new Date(date);
    const isInLast30Days = currentDate >= cutoffDate;
    const hasPressure = jsonData.pressures[i] != null;

    // Calculate trend for last 30 days, starting from the last pressure point and working backwards
    let trendRuntime = null;
    if (isInLast30Days && hasPressure && lastPressureIndex >= 0) {
      // Start from the last pressure point and work backwards using the slope
      const runtimeDiff = lastCumulativeRuntime - jsonData.cumulative_runtimes[i];
      trendRuntime = lastPressureValue - (jsonData.runtime_trend_line.slope * runtimeDiff);
    }

    return {
      date: date,
      pressure: jsonData.pressures[i],
      runtime: jsonData.cumulative_runtimes[i], // Already in hours from backend
      trendRuntime
    };
  });

  const csvColumns: CsvColumn<(typeof chartData)[number]>[] = [
    { header: 'Date', value: 'date' },
    { header: 'Cumulative runtime (hours)', value: 'runtime' },
    { header: 'Pressure (Pa)', value: 'pressure' },
    { header: 'Runtime trend 30d (Pa)', value: 'trendRuntime' },
    { header: 'Notice target (Pa)', value: () => jsonData.targets.notice },
    { header: 'Change target (Pa)', value: () => jsonData.targets.change },
  ];

  // Both axes share one explicit pair over the two drawn lines (FS-709),
  // fitted the way the pressure chart fits its own. Unchecked hugs the lines
  // and stretches to keep both target lines in frame: they default to
  // ifOverflow="discard", and the old 'auto' domain kept one only when it
  // happened to fall inside recharts' rounding. Checked reaches zero, and
  // still drops below it if any data does.
  const [dataMin, dataMax] = measureRange(chartData, /^(pressure|trendRuntime)$/);
  const yDomain =
    includeZero || !Number.isFinite(dataMin)
      ? sharedPaDomain(dataMin, dataMax, includeZero)
      : bandDomain(dataMin, dataMax, [jsonData.targets.notice, jsonData.targets.change]);

  // Calculate max runtime with 1% buffer (already in hours)
  const maxRuntime = Math.max(...jsonData.cumulative_runtimes);
  const bufferedMax = Math.ceil(maxRuntime * 1.01);

  const formatTooltip = (value: ValueType | undefined, name: NameType | undefined): [React.ReactNode, NameType] => {
    const seriesName = name ?? '';
    if (value === undefined) return ['-', seriesName];

    if (seriesName === 'Runtime' || seriesName === 'Runtime Trend') {
      const hours = typeof value === 'number' ? value.toFixed(1) : value;
      return [`${hours} hrs`, seriesName];
    }

    const val = typeof value === 'number' ? value.toFixed(2) : value;
    return [`${val} Pa`, seriesName];
  };

  return (
    <div className="w-full mt-4">
      <div className="flex items-start justify-between gap-2 mb-8">
        <h3 className="text-lg font-semibold">Pressure vs Cumulative Runtime</h3>
        <ChartCsvButton chart="cumulative-runtime" rows={chartData} columns={csvColumns} />
      </div>
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 90, left: 50, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" yAxisId="left" />
              <XAxis
                type="number"
                dataKey="runtime"
                domain={[0, bufferedMax]}
                tick={{ fontSize: 12 }}
                tickMargin={X_TICK_MARGIN}
                tickFormatter={(value) => value.toFixed(1)}
                label={{
                  value: 'Cumulative Runtime (hours)',
                  position: 'bottom',
                  offset: 40
                }}
              />
              <YAxis
                yAxisId="left"
                domain={yDomain}
                allowDataOverflow
                label={{
                  value: 'Pressure (Pa)',
                  angle: -90,
                  position: 'insideLeft',
                  style: AXIS_TITLE_STYLE,
                  offset: -35
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={yDomain}
                allowDataOverflow
                tick={{ fontSize: 12 }}
                label={{
                  value: 'Pressure (inH₂O)',
                  angle: 90,
                  position: 'insideRight',
                  style: AXIS_TITLE_STYLE,
                  offset: -35
                }}
                tickFormatter={inH2OTickFormatter(yDomain)}
              />
              <Tooltip
                formatter={formatTooltip}
                labelFormatter={(label, payload) => {
                  const dataPoint = payload?.[0]?.payload;
                  const hours = `${Number(label).toFixed(2)} hrs`;
                  const date = dataPoint?.date || '';
                  return [hours, date];
                }}
              />
              <Legend verticalAlign="top" height={20} itemSorter={null} />
              <ReferenceLine
                y={jsonData.targets.notice}
                zIndex={REFERENCE_LINE_Z_INDEX}
                stroke="orange"
                strokeDasharray="3 3"
                label={referenceLabel('Notice Target', jsonData.targets.notice, yDomain, 'below')}
                yAxisId="left"
              />
              <ReferenceLine
                y={jsonData.targets.change}
                zIndex={REFERENCE_LINE_Z_INDEX}
                stroke="red"
                strokeDasharray="3 3"
                label={referenceLabel('Change Target', jsonData.targets.change, yDomain, 'above')}
                yAxisId="left"
              />
              <Line
                type="monotone"
                dataKey="pressure"
                zIndex={DATA_LINE_Z_INDEX}
                stroke="#2563eb"
                dot={{ r: 2 }}
                activeDot={activeDot}
                name="Pressure"
                yAxisId="left"
                strokeWidth={2}
                connectNulls={true}
              />
              <Line
                type="monotone"
                dataKey="trendRuntime"
                zIndex={DATA_LINE_Z_INDEX}
                stroke="#10b981"
                strokeDasharray="5 5"
                dot={false}
                activeDot={activeDot}
                name="Runtime Trend (30d)"
                yAxisId="left"
                strokeWidth={2}
                connectNulls={true}
              />
            </LineChart>
          </ResponsiveContainer>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 select-none cursor-pointer w-fit">
            <Checkbox
              className="size-6"
              checked={includeZero}
              onCheckedChange={toggleIncludeZero}
              title="Include zero on the pressure axis"
            />
            Include zero on the pressure axis
          </label>
        </div>
      </div>
    </div>
  );
};

export default CumulativeRuntimeChart;
